import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import {
  readAllTemplatesActiveView,
  writeAllTemplatesActiveView,
} from "../../../utils/all-templates-active-view";
import {
  calculateFreeTrialDaysLeft,
  isPullActiveCoach,
} from "../../../utils/user-helpers";
import CustomizeTableModal from "../challenge-report/components/CustomizeTableModal";
import CheckInTemplates from "./components/CheckInTemplates";
import { ClientConfigurationTemplates } from "./components/client-config/ClientConfigurationTemplates";
import DailyAccountabilityTemplates from "./components/DailyAccountabilityTemplates";
import HeaderTemplates from "./components/HeaderTemplates";
import LeaderboardTemplates from "./components/LeaderboardTemplates";
import MealPlanTemplates from "./components/MealPlanTemplates";
import MealPlansLanding from "./components/meal-plans-landing/MealPlansLanding";
import ProgramsTemplates from "./components/ProgramsTemplates";
import SingleWorkoutsTemplates from "./components/SingleWorkoutsTemplates";
import TeamsPrograms from "./components/TeamsPrograms";
import TemplateCard from "./components/TemplateCard";

const TEMPLATE_ACTION = {
  VIEW: "view",
  SUBMIT: "submit",
};

const getTemplatesCards = (t, referAsClients) => {
  const role = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");
  return [
    {
      id: "programs-template",
      title: t("allTemplates.cards.trainingPrograms.title"),
      subtitle: t("allTemplates.cards.trainingPrograms.subtitle"),
      description: t("allTemplates.cards.trainingPrograms.description", {
        role,
      }),
      colors: ["#5B86E5", "#36D1DC"],
      iconSrc: "/img/new-icons/albums.svg",
      icon: <SvgIcon src="/img/new-icons/albums.svg" noFilterOnLight={true} />,
      redirectTo: "/coaching-portal/templates/programs",
      buttonText: t("allTemplates.cards.trainingPrograms.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "build-step-one",
            cycleType: "new",
            isTemplate: true,
          },
        });
      },
      action: TEMPLATE_ACTION.VIEW,
      view: "programs",
      tab: "programs-tab",
    },
    {
      id: "single-workouts-template",
      title: t("allTemplates.cards.singleWorkouts.title"),
      subtitle: t("allTemplates.cards.singleWorkouts.subtitle"),
      description: t("allTemplates.cards.singleWorkouts.description", {
        role,
      }),
      colors: ["#FF512F", "#F09819"],
      iconSrc: "/img/new-icons/dumbbell-dark.svg",
      icon: (
        <SvgIcon
          src="/img/new-icons/dumbbell-dark.svg"
          classNames="single-workouts-template-icon"
          noFilterOnLight={true}
        />
      ),
      buttonText: t("allTemplates.cards.singleWorkouts.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "build-step-one",
            cycleType: "new",
            isTemplate: true,
            storeType: "single-workout",
            fromTemplate: true,
          },
        });
      },
      redirectTo: "/coaching-portal/templates/single-workouts",
      action: TEMPLATE_ACTION.VIEW,
      view: "single-workouts",
      tab: "single-workouts-tab",
    },
    {
      id: "teams-programs-template",
      title: t("allTemplates.cards.teamsPrograms.title"),
      subtitle: t("allTemplates.cards.teamsPrograms.subtitle"),
      description: t("allTemplates.cards.teamsPrograms.description"),
      colors: ["#DA3462", "#FCB77E"],
      icon: (
        <SvgIcon
          src="/img/new-icons/people-circle-outline.svg"
          noFilterOnLight={true}
        />
      ),
      buttonText: t("allTemplates.cards.teamsPrograms.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "create-team",
          },
        });
      },
      redirectTo: "/coaching-portal/templates/teams-programs",
      action: TEMPLATE_ACTION.VIEW,
      view: "teams-programs",
      tab: "teams-programs-tab",
    },
    {
      id: "check-in-templates",
      title: t("allTemplates.cards.checkInQuestionnaire.title"),
      subtitle: t("allTemplates.cards.checkInQuestionnaire.subtitle"),
      description: t("allTemplates.cards.checkInQuestionnaire.description", {
        role,
      }),
      colors: ["#11998E", "#38EF7D"],
      iconSrc: "/img/new-icons/document-text.svg",
      icon: (
        <SvgIcon
          src="/img/new-icons/document-text.svg"
          noFilterOnLight={true}
        />
      ),
      buttonText: t("allTemplates.cards.checkInQuestionnaire.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "add-questionnaire-template",
            section: "questionnaire",
          },
        });
      },
      action: TEMPLATE_ACTION.VIEW,
      view: "check-in",
      tab: "check-in-tab",
    },
    {
      id: "daily-accountability-templates",
      title: t("allTemplates.cards.dailyAccountability.title"),
      subtitle: t("allTemplates.cards.dailyAccountability.subtitle"),
      description: t("allTemplates.cards.dailyAccountability.description", {
        role,
      }),
      colors: ["#F37335", "#FDC830"],
      iconSrc: "/img/new-icons/list-circle.svg",
      icon: (
        <SvgIcon src="/img/new-icons/list-circle.svg" noFilterOnLight={true} />
      ),
      buttonText: t("allTemplates.cards.dailyAccountability.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "add-accountability-template",
            section: "accountability",
          },
        });
      },
      redirectTo: "accountability-templates",
      action: TEMPLATE_ACTION.VIEW,
      section: "accountability",
      view: "daily-accountability",
      tab: "daily-accountability-tab",
    },
    {
      id: "leaderboard-templates",
      title: t(
        "allTemplates.cards.leaderboardTemplates.title",
        "Leaderboard templates"
      ),
      subtitle: t(
        "allTemplates.cards.leaderboardTemplates.subtitle",
        "Customize report columns"
      ),
      description: t(
        "allTemplates.cards.leaderboardTemplates.description",
        "Save custom column selections for the leaderboard report."
      ),
      colors: ["#1A1A2E", "#16213E"],
      iconSrc: "/img/new-icons/stats-chart.svg",
      icon: (
        <SvgIcon
          src="/img/new-icons/stats-chart-lg.svg"
          noFilterOnLight={true}
        />
      ),
      buttonText: t(
        "allTemplates.cards.leaderboardTemplates.buttonText",
        "Manage"
      ),
      action: TEMPLATE_ACTION.VIEW,
      view: "leaderboard",
      tab: "leaderboard-tab",
    },
    {
      id: "meal-plans-template",
      title: t("allTemplates.cards.mealPlans.title"),
      subtitle: t("allTemplates.cards.mealPlans.subtitle"),
      description: t("allTemplates.cards.mealPlans.description", { role }),
      colors: ["#654EA3", "#EAAFC8"],
      iconSrc: "/img/new-icons/restaurant.svg",
      icon: (
        <SvgIcon src="/img/new-icons/restaurant.svg" noFilterOnLight={true} />
      ),
      redirectTo: "/coaching-portal/templates/meal-plans",
      buttonText: t("allTemplates.cards.mealPlans.buttonText"),
      // buttonAction: () => {
      //   submit(`/actions/refresh/?`, {
      //     method: "GET",
      //     body: {
      //       "modal[type]": "new-meal-plan-template",
      //     },
      //   });
      // },
      action: TEMPLATE_ACTION.VIEW,
      view: "meal-plans",
      tab: "meal-plans-tab",
    },
    {
      id: "client-configuration-templates",
      title: t("allTemplates.cards.clientConfiguration.title"),
      subtitle: t("allTemplates.cards.clientConfiguration.subtitle"),
      description: t("allTemplates.cards.clientConfiguration.description"),
      colors: ["#B91D73", "#F953C6"],
      iconSrc: "/img/new-icons/phone-portrait-outline.svg",
      icon: (
        <SvgIcon
          src="/img/new-icons/phone-portrait-outline.svg"
          noFilterOnLight={true}
        />
      ),
      buttonText: t("allTemplates.cards.clientConfiguration.buttonText"),
      buttonAction: () => {
        submit(`/actions/refresh/?`, {
          method: "GET",
          body: {
            "modal[type]": "add-client-configuration-template",
            tabView: "client-configuration-tab",
            templateView: "client-configuration",
          },
        });
      },
      action: TEMPLATE_ACTION.VIEW,
      view: "client-configuration",
      tab: "client-configuration-tab",
    },
  ];
};

const AllTemplatesView = (props) => {
  const { t } = useTranslation("coaching-portal");
  const context = useRillContext();

  const { locals } = context;
  const {
    cards,
    activeUser,
    mealPlanTemplates,
    query,
    mealPlanCountDetailed,
    mealPlanCountSimplified,
    activeUserToRequest,
    teamPrograms,
    clientConfigurationTemplates = [],
    challengeReportTemplates,
    leaderboardBoards,
  } = locals;
  const { webAppTheme } = activeUser;
  const isDarkTheme = !webAppTheme || webAppTheme === "dark";
  const { questionnaireTemplates, accountabilityTemplates } =
    activeUserToRequest;
  const { tabView, templateView } = query;
  const referAsClients = Boolean(activeUser.referAsClients);
  const templatesCards = getTemplatesCards(t, referAsClients);
  const { total, pageSize } = props;
  // `templateView` is only trustworthy when present: entering a sub-view pushes
  // it onto the URL via a raw `history.pushState` (see `handleTemplateClick`
  // below) that Rill's browser adapter never observes, so it keeps using the
  // URL from the last *real* navigation as the `Referrer` for any later
  // `/actions/refresh` request (e.g. a modal opening or saving inside a
  // template). That stale referrer lacks `tabView`/`templateView`, so the
  // `query` this component re-renders with after such a round trip does too -
  // sessionStorage is the fallback so those round trips don't kick the coach
  // back out to the Templates grid.
  const getPersistedActiveView = () => {
    const persisted = readAllTemplatesActiveView();
    if (!persisted) return null;
    const isValid = templatesCards.some((c) => c.view === persisted.activeView);
    return isValid ? persisted : null;
  };
  const resolveViewState = () => {
    if (templateView) {
      const resolvedView =
        templatesCards.find((c) => c.view === templateView)?.view ?? null;
      return { tabState: tabView ?? "programs-tab", activeView: resolvedView };
    }
    const persisted = getPersistedActiveView();
    return {
      tabState: persisted?.tabState ?? "programs-tab",
      activeView: persisted?.activeView ?? null,
    };
  };
  const [tabState, setTabState] = useState(() => resolveViewState().tabState);
  const [activeView, setActiveView] = useState(
    () => resolveViewState().activeView
  );
  const [leaderboardTemplate, setLeaderboardTemplate] = useState(null);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);

  // Client-side navigation (e.g. the leftbar "Templates" link) re-renders this
  // component with a fresh `query` object instead of remounting it, so `tabState`/
  // `activeView` must be resynced here or they stay stuck on whatever sub-view
  // was active before the navigation. Depending on `query` itself (a new object
  // reference on every Rill navigation, but the same reference across the local
  // pushState-only updates from `handleTemplateClick`/`handleSetActiveView`) is
  // what makes this fire only when it should.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on the `query` object identity, not its derived tabView/templateView/templatesCards values
  useEffect(() => {
    const resolved = resolveViewState();
    setTabState(resolved.tabState);
    setActiveView(resolved.activeView);
    if (templateView) {
      writeAllTemplatesActiveView(
        resolved.activeView
          ? { tabState: resolved.tabState, activeView: resolved.activeView }
          : null
      );
    }
  }, [query]);

  const openLeaderboardModal = (template = null) => {
    setLeaderboardTemplate(template);
    setShowLeaderboardModal(true);
  };

  const handleSaveLeaderboardTemplate = ({
    name,
    columns,
    measurements,
    scopeTeamPrograms,
    repMaxExercises,
    isDefault,
    existingId,
  }) => {
    submit("/actions/challenge-report-templates", {
      method: "POST",
      body: {
        ...(existingId ? { id: existingId } : {}),
        name,
        isDefault: !!isDefault,
        columns: [...columns],
        bodyMeasurements: [...measurements],
        repMaxExercises: repMaxExercises || [],
        scope: { teamPrograms: scopeTeamPrograms || [] },
      },
    });
  };

  const leaderboardProgramOptions = [
    ...new Set(
      [...(teamPrograms?.published || []), ...(teamPrograms?.draft || [])]
        .map((p) => p.name)
        .filter(Boolean)
    ),
  ].sort();

  const daysLeft = calculateFreeTrialDaysLeft(activeUserToRequest);
  const isPushCoachWithEndedTrial =
    activeUserToRequest.permissions?.includes("coach") &&
    activeUserToRequest.pushAccountInfo &&
    daysLeft < 1 &&
    !isPullActiveCoach(activeUserToRequest);

  const TAB_VIEW_CONFIG = {
    "programs-tab": {
      title: t("allTemplates.tabs.programs"),
      renderContent: () => renderProgramsContent(),
    },
    "check-in-tab": {
      title: t("allTemplates.tabs.checkInQuestionnaire"),
      renderContent: () =>
        isPushCoachWithEndedTrial ? null : (
          <CheckInTemplates
            locals={locals}
            templates={questionnaireTemplates?.templates || []}
          />
        ),
    },
    "daily-accountability-tab": {
      title: t("allTemplates.tabs.dailyAccountability"),
      renderContent: () =>
        isPushCoachWithEndedTrial ? null : (
          <DailyAccountabilityTemplates
            locals={locals}
            templates={accountabilityTemplates?.templates || []}
          />
        ),
    },
    "meal-plans-tab": {
      title: t("allTemplates.tabs.mealPlans"),
      renderContent: () =>
        query.planMode ? (
          <MealPlanTemplates
            locals={locals}
            templates={mealPlanTemplates || []}
          />
        ) : (
          <MealPlansLanding
            mealPlanCountDetailed={mealPlanCountDetailed}
            mealPlanCountSimplified={mealPlanCountSimplified}
            bannerTitle={selectedTemplate?.title}
            bannerSubtitle={selectedTemplate?.description}
            bannerIcon={selectedTemplate?.iconSrc}
            bannerGradientStart={selectedTemplate?.colors?.[0]}
            bannerGradientEnd={selectedTemplate?.colors?.[1]}
            onBack={() => handleSetActiveView(null)}
          />
        ),
    },
    "teams-programs-tab": {
      title: t("allTemplates.tabs.teamsPrograms"),
      renderContent: () => <TeamsPrograms locals={locals} />,
    },
    "client-configuration-tab": {
      title: t("allTemplates.tabs.clientConfiguration"),
      renderContent: () => <ClientConfigurationTemplates locals={locals} />,
    },
    "leaderboard-tab": {
      title: t("allTemplates.tabs.leaderboard", "Leaderboard templates"),
      renderContent: () => (
        <LeaderboardTemplates
          locals={locals}
          templates={challengeReportTemplates || []}
          boards={leaderboardBoards?.boards || []}
          leaderboardEnabled={Boolean(activeUserToRequest.leaderboard)}
          onEdit={(tpl) => openLeaderboardModal(tpl)}
        />
      ),
    },
    "single-workouts-tab": {
      title: t("allTemplates.tabs.singleWorkouts"),
      renderContent: () => <SingleWorkoutsTemplates locals={locals} />,
    },
  };

  const currentTabConfig =
    TAB_VIEW_CONFIG[tabState] ?? TAB_VIEW_CONFIG["programs-tab"];
  const selectedTemplate = templatesCards.find((c) => c.tab === tabState);
  const templates = tabState === "programs-tab" ? cards : mealPlanTemplates;
  const athleteLabel = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");
  const descriptionText = t("allTemplates.descriptionWithRole", {
    role: athleteLabel,
  });

  const mealPlanAIApproved = activeUserToRequest?.mealPlanAIApproved;
  const hasTemplates =
    templates && Array.isArray(templates) && templates.length > 0;

  const renderProgramsContent = () => {
    if (hasTemplates) {
      return (
        <ProgramsTemplates
          locals={locals}
          cards={cards}
          total={total}
          pageSize={pageSize}
        />
      );
    }
    return (
      <div className="no-programs-section">
        <p>{t("allTemplates.noTemplatesAdded")}</p>
        <span>{descriptionText}</span>
        <a
          role="button"
          data-button-type="primary"
          href={`/actions/refresh/?modal[type]=build-step-one&cycleType=new&isTemplate=true`}
        >
          {t("allTemplates.addTemplate")}
        </a>
      </div>
    );
  };

  const isMealPlansLanding = tabState === "meal-plans-tab" && !query.planMode;

  const handleHeaderBack = () => {
    if (tabState === "meal-plans-tab") {
      // Return to the meal-plans landing instead of the top-level grid.
      submit("/actions/refresh", {
        method: "GET",
        body: { planMode: "", page: "" },
      });
      return;
    }
    handleSetActiveView(null);
  };

  const handleSetActiveView = (view) => {
    setActiveView(view);
    const url = new URL(window.location.href);
    if (view === null) {
      url.searchParams.delete("templateView");
      url.searchParams.delete("tabView");
      writeAllTemplatesActiveView(null);
    }
    window.history.pushState({}, "", url.toString());
  };

  const handleTemplateClick = (template) => {
    if (template.action === TEMPLATE_ACTION.VIEW) {
      const url = new URL(window.location.href);
      url.searchParams.set("tabView", template.tab);
      url.searchParams.set("templateView", template.view);
      window.history.pushState({}, "", url.toString());
      setActiveView(template.view);
      setTabState(template.tab);
      writeAllTemplatesActiveView({
        tabState: template.tab,
        activeView: template.view,
      });
    } else if (template.action === TEMPLATE_ACTION.SUBMIT) {
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          "modal[type]": template.redirectTo,
          section: template.section,
        },
      });
    }
  };

  const getTemplateCount = (templateId) => {
    if (templateId === "programs-template") {
      return total || cards.length;
    } else if (templateId === "meal-plans-template") {
      return (
        (mealPlanCountDetailed || 0) + (mealPlanCountSimplified || 0) ||
        mealPlanTemplates.length
      );
    } else if (templateId === "check-in-templates") {
      return questionnaireTemplates?.templates?.length || 0;
    } else if (templateId === "daily-accountability-templates") {
      return accountabilityTemplates?.templates?.length || 0;
    } else if (templateId === "teams-programs-template") {
      return (
        (teamPrograms?.published?.length || 0) +
          (teamPrograms?.draft?.length || 0) || 0
      );
    } else if (templateId === "client-configuration-templates") {
      const coachId = String(activeUserToRequest?._id ?? "");
      return clientConfigurationTemplates.filter(
        (template) =>
          template?.templateType === "coach" &&
          String(template?.ownerId ?? "") === coachId
      ).length;
    } else if (templateId === "leaderboard-templates") {
      return challengeReportTemplates?.length || 0;
    } else if (templateId === "single-workouts-template") {
      return locals.savedWorkouts?.length || 0;
    }
    return 0;
  };

  return (
    <main className="coaching-programs">
      <input type="checkbox" id="mobile-filter-toggle" />
      {activeView !== null && !isMealPlansLanding && !query.folderId && (
        <HeaderTemplates
          tabState={tabState}
          setTabState={setTabState}
          query={query}
          onBack={handleHeaderBack}
          bannerTitle={selectedTemplate?.title}
          bannerSubtitle={selectedTemplate?.description}
          bannerIcon={selectedTemplate?.iconSrc}
          bannerGradientStart={selectedTemplate?.colors?.[0]}
          bannerGradientEnd={selectedTemplate?.colors?.[1]}
          count={getTemplateCount(selectedTemplate?.id)}
          generateAIMealPlanAvailable={locals.generateAIMealPlanAvailable}
          mealPlanAIApproved={mealPlanAIApproved}
          onAddLeaderboard={() => openLeaderboardModal(null)}
          referAsClients={Boolean(activeUserToRequest?.referAsClients)}
          coachId={String(activeUserToRequest?._id || "")}
        />
      )}

      {activeView === null ? (
        <>
          <header className="header-coaching-programs">
            <hgroup className="hgroup-templates">
              <div className="header-text">
                <h3>{t("allTemplates.pageTitle")}</h3>
                <p className="p4 opacity-70">
                  {t("allTemplates.pageDescription")}
                </p>
              </div>
            </hgroup>
          </header>

          <div className="templates-cards-grid">
            {templatesCards
              .filter(
                (template) =>
                  !isPushCoachWithEndedTrial ||
                  (template.id !== "check-in-templates" &&
                    template.id !== "daily-accountability-templates")
              )
              .map((template) => (
                <div key={template.id}>
                  <TemplateCard
                    title={template.title}
                    subtitle={template.subtitle}
                    description={template.description}
                    count={getTemplateCount(template.id)}
                    colors={template.colors}
                    icon={template.icon}
                    isDarkTheme={isDarkTheme}
                    onClick={() => handleTemplateClick(template)}
                    buttonText={template.buttonText}
                    buttonAction={template.buttonAction}
                  />
                </div>
              ))}
          </div>
        </>
      ) : (
        <>{currentTabConfig.renderContent()}</>
      )}
      <CustomizeTableModal
        isOpen={showLeaderboardModal}
        onClose={() => setShowLeaderboardModal(false)}
        selectedColumns={leaderboardTemplate?.columns || []}
        selectedMeasurements={leaderboardTemplate?.bodyMeasurements || []}
        initialRepMaxExercises={leaderboardTemplate?.repMaxExercises || []}
        initialName={leaderboardTemplate?.name || ""}
        initialScopeTeamPrograms={
          leaderboardTemplate?.scope?.teamPrograms || []
        }
        initialIsDefault={!!leaderboardTemplate?.isDefault}
        existingId={leaderboardTemplate?._id}
        programOptions={leaderboardProgramOptions}
        onSaveTemplate={handleSaveLeaderboardTemplate}
      />
    </main>
  );
};

AllTemplatesView.propTypes = {
  total: PropTypes.number,
  pageSize: PropTypes.number,
};

export default AllTemplatesView;
